import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { downloadResultVideo, fetchGazeResultDetail } from '../api';
import type { GazeTestResultDetail, GazeTestTaskBreakdown, IrisParentIntakeV1 } from '../types';
import {
  formatScreeningLabel,
  isRiskLabel,
  screeningRiskConfidence,
} from '../utils/screeningLabel';
import { productTokens } from '../theme';

// --- Utility Functions ---
function formatTimestamp(ts: string): string {
  const dt = new Date(ts);
  if (Number.isNaN(dt.getTime())) return ts;
  return dt.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function parseDob(value: string): { year: number; month: number; day: number } | null {
  const trimmed = value.trim();
  const m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(trimmed);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dobDate = new Date(year, month - 1, day);
  if (Number.isNaN(dobDate.getTime())) return null;
  if (
    dobDate.getFullYear() !== year ||
    dobDate.getMonth() !== month - 1 ||
    dobDate.getDate() !== day
  )
    return null;
  return { year, month, day };
}

function normalizeDobDisplay(value: string): string | null {
  const parts = parseDob(value);
  if (!parts) return null;
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function calcAgeMonths(isoDate: string): number | null {
  const parts = parseDob(isoDate);
  if (!parts) return null;
  const { year, month, day } = parts;
  const now = new Date();
  const dobDate = new Date(year, month - 1, day);
  if (now.getTime() < dobDate.getTime()) return null;
  let months =
    (now.getFullYear() - dobDate.getFullYear()) * 12 + (now.getMonth() - dobDate.getMonth());
  if (now.getDate() < dobDate.getDate()) months -= 1;
  return Math.max(0, months);
}

function isParentIntakeV1(value: unknown): value is IrisParentIntakeV1 {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return obj.version === 1 && typeof obj.dob === 'string' && typeof obj.ageMonths === 'number';
}

function mapBornEarly(value?: string, weeksEarly?: number | null): string {
  if (value === 'no') return '否';
  if (value === 'unsure') return '不确定';
  if (value === 'yes') {
    if (typeof weeksEarly === 'number' && Number.isFinite(weeksEarly) && weeksEarly > 0) {
      return `是（早产${weeksEarly}周）`;
    }
    return '是';
  }
  return '未提供';
}

function mapVision(value?: string, details?: IrisParentIntakeV1['visionDetails']): string {
  if (value === 'no') return '否';
  if (value === 'unsure') return '不确定';
  if (value !== 'yes') return '未提供';
  const parts: string[] = [];
  if (details?.wearsGlasses) parts.push('佩戴眼镜');
  if (details?.eyeTurn) parts.push('眼位偏斜');
  if (typeof details?.other === 'string' && details.other.trim().length > 0) {
    parts.push(`其他`);
  }
  return parts.length ? `是（${parts.join(', ')}）` : '是';
}

function mapDiagnosisStatus(value?: string | null): string {
  if (value === 'asd') return '已确认 ASD';
  if (value === 'non_asd') return '已正式排除 ASD';
  if (value === 'suspected_asd') return '疑似 / 评估中';
  if (value === 'not_yet_confirmed') return '暂未确认';
  return '未提供';
}

function riskPercentage(task: GazeTestTaskBreakdown): number {
  if (typeof task.prob === 'number') return Math.round(task.prob * 100);
  return Math.round(screeningRiskConfidence(task.label, task.confidence) * 100);
}

function thresholdPercentage(task: GazeTestTaskBreakdown): number | null {
  if (typeof task.threshold !== 'number') return null;
  return Math.round(Math.max(0, Math.min(1, task.threshold)) * 100);
}

// --- Demo Data ---
const DEMO_RESULT_DETAIL: GazeTestResultDetail = {
  id: 40992,
  name: '受试儿童-乙',
  label: 'Low Risk',
  confidence: 0.88,
  timestamp: new Date().toISOString(),
  static: {
    version: 1,
    name: '受试儿童-乙',
    dob: '2024-05-10',
    ageMonths: 22,
    bornEarly: 'no',
    visionConcerns: 'no',
    communication: 'few_single_words',
    lastSlept: '2to6h',
  },
  status: 'scored',
  tasks: [
    {
      task: '联合注意力范式评估 (JA)',
      label: 'Low Risk',
      confidence: 0.91,
      prob: 0.12,
      threshold: 0.5,
    },
    {
      task: '社交响应范式评估 (SR)',
      label: 'At Risk',
      confidence: 0.72,
      prob: 0.65,
      threshold: 0.6,
    },
  ],
  timeline: null,
};

export default function ResultDetail(): JSX.Element {
  const { resultId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<GazeTestResultDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const isDemo = resultId === 'demo' || location.pathname === '/results/demo';

  useEffect(() => {
    if (isDemo) {
      setDetail(DEMO_RESULT_DETAIL);
      setError(null);
      setLoading(false);
      return;
    }

    const idNum = Number(resultId);
    if (!Number.isFinite(idNum)) {
      setError('无效的报告编号');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    fetchGazeResultDetail(idNum)
      .then((resp) => {
        setDetail(resp);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '无法获取报告数据');
        setLoading(false);
      });
  }, [isDemo, resultId]);

  const hasScore = useMemo(() => {
    if (!detail) return false;
    const status = String(detail.status || '').toLowerCase();
    const hasConfidence =
      typeof detail.confidence === 'number' && Number.isFinite(detail.confidence);
    return status === 'scored' && hasConfidence && (detail.label || '').trim().length > 0;
  }, [detail]);

  const percent = useMemo(() => {
    if (!detail || !hasScore || typeof detail.confidence !== 'number') return 0;
    return Math.round(Math.max(0, Math.min(1, detail.confidence)) * 100);
  }, [detail, hasScore]);

  const patientName = useMemo(() => {
    const direct = detail?.name;
    if (typeof direct === 'string' && direct.trim().length > 0) return direct.trim();
    const raw = detail?.static ? (detail.static as Record<string, unknown>)['name'] : undefined;
    if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
    return '未提供';
  }, [detail]);

  const intake = useMemo<IrisParentIntakeV1 | null>(() => {
    if (!detail?.static) return null;
    const raw = detail.static as unknown;
    if (!isParentIntakeV1(raw)) return null;
    return raw;
  }, [detail]);

  const dobDisplay = useMemo(() => {
    if (typeof intake?.dob === 'string') {
      const normalized = normalizeDobDisplay(intake.dob);
      return normalized ?? intake.dob.trim() ?? '未提供';
    }
    const rawDob = detail?.static ? (detail.static as Record<string, unknown>)['dob'] : undefined;
    if (typeof rawDob !== 'string') return '未提供';
    const trimmed = rawDob.trim();
    if (!trimmed) return '未提供';
    const normalized = normalizeDobDisplay(trimmed);
    return normalized ?? trimmed;
  }, [detail, intake]);

  const fallbackAgeMonths = useMemo(() => {
    if (!detail?.static) return null;
    const rawDob = (detail.static as Record<string, unknown>)['dob'];
    if (typeof rawDob !== 'string') return null;
    return calcAgeMonths(rawDob);
  }, [detail]);

  const handleBack = () => {
    if (isDemo) {
      navigate(window.history.length > 1 ? -1 : '/login');
      return;
    }
    navigate(-1);
  };

  const handleDownloadVideo = async () => {
    if (!detail?.id || !detail.video) return;
    setDownloadingVideo(true);
    try {
      await downloadResultVideo(detail.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : '无法下载原始视频');
    } finally {
      setDownloadingVideo(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={48} thickness={4} />
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        height: '100%',
        minHeight: 0,
        py: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }}>
        <Button
          variant="text"
          onClick={handleBack}
          sx={{ fontWeight: 600, color: 'text.secondary' }}
        >
          返回列表
        </Button>
        <Stack direction="row" spacing={2}>
          {detail?.video ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleDownloadVideo()}
              disabled={downloadingVideo}
            >
              {downloadingVideo ? '下载中…' : '下载原始视频'}
            </Button>
          ) : null}
          <Button variant="outlined" color="secondary" size="small">
            导出 PDF
          </Button>
          <Button variant="contained" color="secondary" size="small" disableElevation>
            打印报告
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {detail && (
        <Paper
          variant="outlined"
          sx={{
            position: 'relative',
            p: { xs: 2.5, sm: 4 },
            bgcolor: 'background.paper',
            overflow: 'auto',
            borderRadius: 1,
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: 'secondary.main', mb: 0.75 }}
          >
            EyeCI 儿童发育与行为研究平台
          </Typography>
          <Typography variant="h4" sx={{ color: 'text.primary', mb: 2.5 }}>
            {isDemo ? '模拟婴幼儿交互分析报告单' : '婴幼儿交互反应分析报告单'}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Metadata Section */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                儿童标识：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {patientName}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                院内编号：
              </Typography>
              <Typography
                variant="body1"
                component="span"
                fontWeight={700}
                fontFamily={productTokens.monoStack}
              >
                {detail.child?.clinicChildId ?? '未提供'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                修正月龄：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {typeof (intake?.ageMonths ?? fallbackAgeMonths) === 'number'
                  ? `${intake?.ageMonths ?? fallbackAgeMonths} 个月`
                  : '未提供'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                出生日期：
              </Typography>
              <Typography
                variant="body1"
                component="span"
                fontWeight={700}
                fontFamily={productTokens.monoStack}
              >
                {dobDisplay}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                早产史：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {mapBornEarly(intake?.bornEarly, intake?.weeksEarly)}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                样本编号：
              </Typography>
              <Typography
                variant="body1"
                component="span"
                fontWeight={700}
                fontFamily={productTokens.monoStack}
              >
                {isDemo ? 'DEMO-IR8X2' : detail.id.toString().padStart(8, '0')}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" component="span" color="text.secondary">
                视力问题：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {mapVision(intake?.visionConcerns, intake?.visionDetails)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" component="span" color="text.secondary">
                报告时间：
              </Typography>
              <Typography
                variant="body1"
                component="span"
                fontWeight={700}
                fontFamily={productTokens.monoStack}
              >
                {formatTimestamp(detail.timestamp)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" component="span" color="text.secondary">
                参考状态：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {mapDiagnosisStatus(detail.officialDiagnosis?.status)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" component="span" color="text.secondary">
                原始视频：
              </Typography>
              <Typography variant="body1" component="span" fontWeight={700}>
                {detail.video?.sizeBytes
                  ? `已保存 (${Math.round(detail.video.sizeBytes / 1024 / 1024)} MB)`
                  : '未保存'}
              </Typography>
            </Grid>
          </Grid>

          {hasScore ? (
            <>
              {/* Table */}
              <TableContainer
                sx={{
                  mb: 5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(productTokens.iris, 0.05) }}>
                      <TableCell sx={{ fontWeight: 800, py: 1.5 }}>评估项目</TableCell>
                      <TableCell sx={{ fontWeight: 800, py: 1.5 }} align="center">
                        特征异常率
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, py: 1.5 }} align="center">
                        提示
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, py: 1.5 }} align="center">
                        参考阈值
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, py: 1.5 }} align="right">
                        模型置信度
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.tasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, borderBottom: 'none' }}>
                          暂无评估分项数据
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.tasks.map((task, idx) => {
                        const risk = riskPercentage(task);
                        const threshold = thresholdPercentage(task);
                        const isTaskHighRisk = task.label.toLowerCase() === 'at risk';

                        return (
                          <TableRow key={task.task}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                py: 1.5,
                                borderBottom:
                                  idx === detail.tasks.length - 1
                                    ? 'none'
                                    : '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {task.task}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontFamily: productTokens.monoStack,
                                fontWeight: 700,
                                borderBottom:
                                  idx === detail.tasks.length - 1
                                    ? 'none'
                                    : '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {risk.toFixed(1)}%
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                borderBottom:
                                  idx === detail.tasks.length - 1
                                    ? 'none'
                                    : '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {isTaskHighRisk ? (
                                <Typography
                                  component="span"
                                  sx={{ color: 'error.main', fontWeight: 900 }}
                                >
                                  ↑ 阳性
                                </Typography>
                              ) : (
                                <Typography
                                  component="span"
                                  sx={{ color: 'success.main', fontWeight: 700 }}
                                >
                                  阴性
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontFamily: productTokens.monoStack,
                                color: 'text.secondary',
                                borderBottom:
                                  idx === detail.tasks.length - 1
                                    ? 'none'
                                    : '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {threshold !== null ? `< ${threshold.toFixed(1)}%` : '—'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontFamily: productTokens.monoStack,
                                borderBottom:
                                  idx === detail.tasks.length - 1
                                    ? 'none'
                                    : '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              {task.confidence.toFixed(3)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Conclusion */}
              <Box sx={{ mb: 6, position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 900, mb: 1, color: 'secondary.dark' }}
                >
                  【综合分析意见】
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderLeft: '4px solid',
                    borderColor: 'secondary.main',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 800, mb: 1.5, fontSize: '1.1rem' }}>
                    系统测算总体结论：{formatScreeningLabel(detail.label).text}{' '}
                    <Typography
                      component="span"
                      sx={{
                        color: isRiskLabel(detail.label) ? 'error.main' : 'success.main',
                        fontWeight: 900,
                        ml: 1,
                      }}
                    >
                      {isRiskLabel(detail.label) ? '(风险提示)' : '(正常范围)'}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    说明：基于受试儿童在多模态交互范式中的视觉注意力分配、注视转移、瞳孔动态等发育行为特征，经深度学习算法模型测算，当前样本与特定发育迟缓行为模式的整体相似度为{' '}
                    <Typography component="span" fontWeight={800} color="text.primary">
                      {percent}%
                    </Typography>
                    。系统运算综合置信区间为{' '}
                    <Typography component="span" fontWeight={800} color="text.primary">
                      {detail.confidence.toFixed(3)}
                    </Typography>
                    。
                  </Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                mb: 5,
                textAlign: 'center',
                p: 3,
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.secondary', mb: 2 }}>
                【 数据采集状态 】
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                当前评估记录已保存完整摄像头视频、刺激时间线与问卷元数据，用于后端离线训练与验证。
              </Typography>
              <Typography variant="body2" color="text.secondary">
                此样本暂未输出量化分析评级与分项特征阈值数据。
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Signatures & Footer */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 6, px: { xs: 0, sm: 4 } }}
          >
            <Typography variant="body1" fontWeight={700}>
              采集员：
              <span
                style={{
                  display: 'inline-block',
                  width: 100,
                  borderBottom: '1px solid rgba(24, 50, 65, 0.55)',
                }}
              ></span>
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              审核人：
              <span
                style={{
                  display: 'inline-block',
                  width: 100,
                  borderBottom: '1px solid rgba(24, 50, 65, 0.55)',
                }}
              ></span>
            </Typography>
          </Box>
        </Paper>
      )}
    </Container>
  );
}
